import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import Layout from "@/components/Layout";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Camera, Heart, Trophy, Sparkles, Share2, Download, Loader2, Instagram, ArrowLeft } from "lucide-react";

export default function EventRecap() {
  const { eventId } = useParams();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [cardUrl, setCardUrl] = useState("");

  useEffect(() => {
    document.title = "Your night at Eventom";
    if (!user) return;
    api.get(`/recap/${eventId}`)
      .then(({ data }) => setData(data))
      .catch((e) => toast.error(formatApiError(e.response?.data?.detail) || "Recap unavailable"));
  }, [eventId, user]);

  const gen = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/recap/card", { event_id: eventId });
      const abs = data.url.startsWith("http") ? data.url : `${process.env.REACT_APP_BACKEND_URL}${data.url}`;
      setCardUrl(abs);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Card generation failed"); }
    finally { setBusy(false); }
  };

  const share = async () => {
    if (!cardUrl) return;
    try {
      const res = await fetch(cardUrl);
      const blob = await res.blob();
      const file = new File([blob], "eventom-night-recap.png", { type: blob.type || "image/png" });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title: "My Eventom night", text: `My night at ${data?.event?.title} on Eventom ✨` });
        toast.success("Shared");
        return;
      }
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = "eventom-night-recap.png";
      document.body.appendChild(a); a.click(); a.remove();
      toast("Downloaded — upload to your IG Story");
    } catch { toast.error("Share unavailable — long-press to save"); }
  };

  if (!user) return <Layout><section className="max-w-3xl mx-auto px-4 py-12 text-center"><h1 className="font-display text-4xl">Sign in to see your recap</h1><Link to="/login" className="btn-primary mt-4 inline-flex">Sign in</Link></section></Layout>;
  if (!data) return <Layout><div className="max-w-3xl mx-auto px-4 py-16 font-mono text-sm text-muted">Loading your recap…</div></Layout>;

  const ev = data.event;
  return (
    <Layout>
      <section className="max-w-5xl mx-auto px-4 sm:px-6 py-10" data-testid="event-recap">
        <Link to="/profile#tickets" className="inline-flex items-center gap-1 text-[11px] font-mono uppercase tracking-widest text-muted hover:text-lime mb-3"><ArrowLeft className="w-3 h-3" /> Back to my tickets</Link>
        <div className="chip !text-lime !border-lime/40 mb-3"><Sparkles className="w-3 h-3" /> Your Eventom night</div>
        <h1 className="font-fancy uppercase text-4xl md:text-6xl leading-[0.95]">{ev.title}</h1>
        <div className="text-[11px] font-mono uppercase tracking-widest text-muted mt-2">{ev.city} · {new Date(ev.date).toLocaleDateString("en-IN", { day: "2-digit", month: "long", year: "numeric" })}</div>

        <div className="mt-8 grid md:grid-cols-3 gap-4" data-testid="recap-stats">
          <div className="glass rounded-3xl p-6" data-testid="recap-photos">
            <Camera className="w-8 h-8 text-magenta mb-3" />
            <div className="font-fancy text-5xl text-magenta leading-none">{data.photo_count}</div>
            <div className="text-[11px] font-mono uppercase tracking-widest text-muted mt-2">photos captured</div>
            <Link to={`/events/${ev.slug || ev.id}`} className="text-[10px] font-mono uppercase tracking-widest text-lime mt-3 inline-flex">Find yours in AI Gallery →</Link>
          </div>
          <div className="glass rounded-3xl p-6" data-testid="recap-matches">
            <Heart className="w-8 h-8 text-pink mb-3" fill="currentColor" />
            <div className="font-fancy text-5xl text-pink leading-none">{data.matches}</div>
            <div className="text-[11px] font-mono uppercase tracking-widest text-muted mt-2">ClubVIP matches</div>
            <Link to="/clubvip" className="text-[10px] font-mono uppercase tracking-widest text-lime mt-3 inline-flex">Open ClubVIP →</Link>
          </div>
          <div className="glass rounded-3xl p-6" data-testid="recap-contests">
            <Trophy className="w-8 h-8 text-lime mb-3" />
            <div className="font-fancy text-5xl text-lime leading-none">{data.contests_won.length}</div>
            <div className="text-[11px] font-mono uppercase tracking-widest text-muted mt-2">contest{data.contests_won.length === 1 ? "" : "s"} won</div>
            {data.contests_won.length > 0 && (
              <div className="text-[10px] font-mono text-lime mt-2 truncate">{data.contests_won.map((c) => c.name).join(", ")}</div>
            )}
          </div>
        </div>

        <div className="mt-10 grid md:grid-cols-[1.2fr_1fr] gap-6">
          <div className="glass rounded-3xl p-6">
            <div className="font-display text-3xl leading-tight">Share the memory.</div>
            <p className="text-sm text-muted mt-2 max-w-md">Generate an AI recap card and drop it on your Instagram Story. Bring friends into the vibe.</p>
            <button onClick={gen} disabled={busy} data-testid="recap-generate" className="btn-primary mt-5">
              {busy ? <><Loader2 className="w-4 h-4 animate-spin" /> Generating your recap…</> : <><Sparkles className="w-4 h-4" /> Generate my recap card</>}
            </button>
            {cardUrl && (
              <div className="mt-4 flex flex-wrap gap-2">
                <button onClick={share} data-testid="recap-share" className="btn-lime"><Share2 className="w-4 h-4" /> Share</button>
                <a href={cardUrl} download="eventom-night-recap.png" target="_blank" rel="noreferrer" data-testid="recap-download" className="btn-ghost"><Download className="w-4 h-4" /> Save</a>
                <a href="https://www.instagram.com/" target="_blank" rel="noreferrer" data-testid="recap-instagram" className="btn-pink"><Instagram className="w-4 h-4" /> Open Instagram</a>
              </div>
            )}
          </div>

          <div className="rounded-3xl overflow-hidden aspect-[4/5] bg-black/50 border border-white/10 flex items-center justify-center" data-testid="recap-card-preview">
            {cardUrl ? (
              <img src={cardUrl} alt="Recap card" className="w-full h-full object-cover" />
            ) : (
              <div className="text-center p-6">
                <Sparkles className="w-10 h-10 text-lime mx-auto mb-3" />
                <div className="font-display text-xl">Your card lands here.</div>
                <div className="text-[11px] font-mono uppercase tracking-widest text-muted mt-2">Tap Generate to start</div>
              </div>
            )}
          </div>
        </div>
      </section>
    </Layout>
  );
}
